import {
  Box, Center, Card, CardBody, Text, Button, Input, Spinner, Heading, Flex, Progress, ScaleFade, useToast, InputGroup, InputRightElement,
} from "@chakra-ui/react";
import {useEffect, useState} from "react";
import {useParams} from "react-router";
import {useAppDataStore} from "../../mobx/appDataStore";

const NOMBRES_PRODUCTO = {netflix_codigo: 'Netflix', disney: 'Disney+', crunchyroll: 'Crunchyroll', chatgpt: 'ChatGPT'}

// tema visual por producto — agregar una entrada acá activa el estilo con pasos para ese producto.
// sin entrada (ej. crunchyroll) cae al estilo neutro de siempre.
// instantaneo: el código no depende de que llegue un correo (ChatGPT es TOTP, siempre
// disponible), así que no tiene sentido la espera de 30s ni el paso de "enviar correo".
const TEMAS = {
  netflix_codigo: {color: '#E50914', hoverColor: '#f6121d', colorScheme: 'red', dominio: 'netflix.com'},
  disney: {color: '#0C63E7', hoverColor: '#1974ff', colorScheme: 'blue', dominio: 'disneyplus.com'},
  crunchyroll: {color: '#F47521', hoverColor: '#ff8a3d', colorScheme: 'orange', dominio: 'crunchyroll.com'},
  chatgpt: {color: '#74AA9C', hoverColor: '#8dc3b3', colorScheme: 'teal', dominio: 'chatgpt.com', instantaneo: true},
}

const Paso = ({numero, tema, children}) => (
  <Flex mb={4} align={'flex-start'}>
    <Center
      minW={7} h={7} borderRadius={'full'} mr={3} mt={0.5}
      bg={tema.color} color={'white'} fontWeight={'bold'} fontSize={'sm'}
    >
      {numero}
    </Center>
    <Box flex={1}>{children}</Box>
  </Flex>
)

export default function MailLinkClient() {
  const params = useParams()
  const appDataStore = useAppDataStore()
  const toast = useToast()
  const [linkInfo, setLinkInfo] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [buscando, setBuscando] = useState(false)
  const [resultado, setResultado] = useState(null)

  const tema = TEMAS[linkInfo?.producto]

  useEffect(() => {
    document.title = 'Tienda Digital'
    appDataStore.get(`/mail-links/${params.token}`)
      .then(res => setLinkInfo(res))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false))
  }, []);

  // espera 30s antes de consultar — le da tiempo al proveedor de enviar el correo con el código
  // después de que el cliente le da clic a "Continuar". No aplica a productos "instantaneo"
  // (ChatGPT): el código es TOTP, no depende de que llegue nada.
  const buscarCodigo = () => {
    setBuscando(true)
    setResultado(null)
    const consultar = () => {
      appDataStore.get(`/mail-links/${params.token}/emails`)
        .then(res => setResultado(res.length ? res[0] : {vacio: true}))
        .catch(err => setResultado({error: err.message}))
        .finally(() => setBuscando(false))
    }
    if (tema?.instantaneo) consultar()
    else setTimeout(consultar, 30000)
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    toast({title: 'Copiado', status: 'success', duration: 2000, isClosable: true})
  }

  return (
    <Center
      minH={'100vh'} p={5}
      bg={tema ? 'black' : 'gray.50'}
      bgGradient={tema ? `radial(circle at top, ${tema.color}40, black 70%)` : undefined}
    >
      <Card
        maxW={'480px'} w={'100%'}
        bg={tema ? '#141414' : 'white'}
        color={tema ? 'white' : 'inherit'}
        borderRadius={tema ? 20 : 'md'}
        border={tema ? '1px solid' : undefined}
        borderColor={tema ? tema.color : undefined}
        boxShadow={tema ? `0 0 40px ${tema.color}59` : 'md'}
      >
        <CardBody p={tema ? 8 : 6}>
          {loading && <Center><Spinner color={tema ? tema.colorScheme + '.500' : undefined}/></Center>}
          {!loading && error && <Text color={'red.400'}>{error}</Text>}
          {!loading && linkInfo && (
            <>
              <Heading size={'md'} mb={5}>{NOMBRES_PRODUCTO[linkInfo.producto] || linkInfo.producto}</Heading>

              {linkInfo.producto === 'crunchyroll' ? null : tema ? (
                <>
                  <Paso numero={1} tema={tema}>
                    <Text>Ingresa a <Text as={'b'}>{tema.dominio}</Text></Text>
                  </Paso>
                  <Paso numero={2} tema={tema}>
                    <Text mb={2}>Coloca el correo mostrado abajo</Text>
                    {linkInfo.correo && (
                      <InputGroup>
                        <Input
                          isReadOnly value={linkInfo.correo}
                          bg={'whiteAlpha.100'} color={'white'} borderColor={'whiteAlpha.300'}
                        />
                        <InputRightElement width={'4.2rem'} mr={1}>
                          <Button size={'sm'} colorScheme={tema.colorScheme} onClick={() => copyToClipboard(linkInfo.correo)}>Copiar</Button>
                        </InputRightElement>
                      </InputGroup>
                    )}
                  </Paso>
                  <Paso numero={3} tema={tema}>
                    <Text>
                      {tema.instantaneo
                        ? <>Cuando te pida el <Text as={'b'}>código de verificación en dos pasos</Text>, solicítalo aquí abajo</>
                        : <>Da clic en <Text as={'b'}>Continuar</Text>, elige <Text as={'b'}>enviar correo</Text> y espera 30 segundos</>}
                    </Text>
                  </Paso>
                  <Paso numero={4} tema={tema}>
                    <Text>Solicita tu código aquí abajo</Text>
                  </Paso>
                </>
              ) : (
                <>
                  <Text mb={3}>Perfil: <Text as={'b'}>{linkInfo.perfil}</Text></Text>
                  {linkInfo.correo && (
                    <Box mb={3}>
                      <Text fontSize={'sm'} mb={1} color={'gray.500'}>Correo</Text>
                      <InputGroup>
                        <Input isReadOnly value={linkInfo.correo}/>
                        <InputRightElement width={'4.2rem'} mr={1}>
                          <Button size={'sm'} colorScheme={'blue'} onClick={() => copyToClipboard(linkInfo.correo)}>Copiar</Button>
                        </InputRightElement>
                      </InputGroup>
                    </Box>
                  )}
                </>
              )}

              {linkInfo.pin && (
                <Text mb={5} color={tema ? 'gray.300' : undefined}>
                  Pin del perfil: <Text as={'b'} color={tema ? 'white' : undefined}>{linkInfo.pin}</Text>
                </Text>
              )}

              <Button
                w={'100%'} mb={4} onClick={buscarCodigo} isLoading={buscando} loadingText={'Buscando...'}
                bg={tema ? tema.color : undefined} colorScheme={tema ? undefined : 'blue'}
                color={tema ? 'white' : undefined}
                _hover={tema ? {bg: tema.hoverColor, transform: 'scale(1.02)'} : undefined}
                transition={'transform 0.15s ease'}
                size={'lg'} borderRadius={tema ? 8 : 'md'}
              >
                {linkInfo.producto === 'crunchyroll' ? 'Consultar correos' : 'Solicitar código'}
              </Button>

              {buscando && <Progress size={'xs'} isIndeterminate colorScheme={tema ? tema.colorScheme : 'blue'} borderRadius={4} mb={2}/>}

              {resultado?.error && <Text color={'red.400'}>{resultado.error}</Text>}
              {resultado?.vacio && <Text color={tema ? 'gray.400' : 'gray.500'}>Aún no llega un correo nuevo, intenta en un momento.</Text>}
              {linkInfo.producto === 'crunchyroll' && resultado?.link && (
                <ScaleFade in={true} initialScale={0.85}>
                  <Box
                    mt={2} p={4} borderRadius={12} textAlign={'center'}
                    bg={tema ? 'blackAlpha.400' : 'gray.50'}
                    border={'1px solid'} borderColor={tema ? tema.color : 'gray.200'}
                  >
                    <Text fontSize={'sm'} mb={3} color={tema ? 'gray.400' : 'gray.500'}>
                      Confirma que fuiste tú quien intentó iniciar sesión
                    </Text>
                    <Button as={'a'} href={resultado.link} target={'_blank'} rel={'noopener noreferrer'} w={'100%'} colorScheme={'green'}>
                      Sí, fui yo
                    </Button>
                  </Box>
                </ScaleFade>
              )}
              {resultado?.code && (
                <ScaleFade in={true} initialScale={0.85}>
                  <Box
                    mt={2} p={4} borderRadius={12} textAlign={'center'}
                    bg={tema ? 'blackAlpha.400' : 'gray.50'}
                    border={'1px solid'} borderColor={tema ? tema.color : 'gray.200'}
                  >
                    <Text fontSize={'sm'} mb={1} color={tema ? 'gray.400' : 'gray.500'}>Tu código</Text>
                    <Text fontSize={'4xl'} fontWeight={'extrabold'} letterSpacing={'0.2em'} color={tema ? tema.color : 'blue.600'}>
                      {resultado.code}
                    </Text>
                    <Button mt={2} size={'sm'} colorScheme={tema ? tema.colorScheme : 'blue'} onClick={() => copyToClipboard(resultado.code)}>
                      Copiar código
                    </Button>
                  </Box>
                </ScaleFade>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </Center>
  )
}
